import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { BadgeCheck, Search, FileDown, ExternalLink, Share2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import ChapterHeading from "@/components/ChapterHeading";
import { FACTCHECK, EJES } from "@/data/content";

const EASE = [0.16, 1, 0.3, 1];

function VerificadoBadge() {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-verde-soft text-verde text-[11px] font-semibold px-2.5 py-1 whitespace-nowrap">
      <BadgeCheck className="w-3.5 h-3.5" aria-hidden="true" />
      Dato Verificado
    </span>
  );
}

function wrapText(ctx, text, x, y, maxWidth, lineHeight, maxLines = 3) {
  const words = text.split(" ");
  const lines = [];
  let line = "";
  for (const w of words) {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = w;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  lines.slice(0, maxLines).forEach((l, i) => ctx.fillText(l, x, y + i * lineHeight));
  return Math.min(lines.length, maxLines);
}

async function buildShareCard(f, ejeNombre, siteUrl) {
  const W = 1080;
  const H = 1350;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");

  ctx.fillStyle = "#C03C54";
  ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = "#8C2236";
  ctx.fillRect(0, 0, W, 300);

  try {
    const logo = new Image();
    logo.src = "/logo-hero.webp";
    await new Promise((res, rej) => {
      logo.onload = res;
      logo.onerror = rej;
    });
    const lh = 190;
    const lw = (logo.width / logo.height) * lh;
    ctx.drawImage(logo, (W - lw) / 2, 55, lw, lh);
  } catch {
    /* sin logo, el diseño sigue funcionando */
  }

  ctx.textAlign = "center";
  ctx.fillStyle = "#F4F0EA";
  ctx.font = "600 30px 'JetBrains Mono', monospace";
  ctx.fillText(`EVIDENCIA · EJE ${f.ejeId}${ejeNombre ? ` · ${ejeNombre.toUpperCase()}` : ""}`, W / 2, 400);

  ctx.fillStyle = "#FFFFFF";
  ctx.font = "800 120px Montserrat, sans-serif";
  ctx.fillText(f.cifra, W / 2, 570);

  ctx.font = "600 46px Montserrat, sans-serif";
  const usedLines = wrapText(ctx, f.logro, W / 2, 680, W - 220, 62, 3);

  const badgeY = 680 + usedLines * 62 + 50;
  const badgeW = 380;
  const badgeH = 76;
  ctx.fillStyle = "#1E7E50";
  ctx.beginPath();
  ctx.roundRect((W - badgeW) / 2, badgeY, badgeW, badgeH, 38);
  ctx.fill();
  ctx.fillStyle = "#FFFFFF";
  ctx.font = "600 30px Montserrat, sans-serif";
  ctx.fillText("✓ DATO VERIFICADO", W / 2, badgeY + 50);

  ctx.fillStyle = "#2D2825";
  ctx.fillRect(0, H - 150, W, 150);
  ctx.fillStyle = "#FFFFFF";
  ctx.font = "600 30px Montserrat, sans-serif";
  ctx.fillText("Verifícala en el portal del Segundo Informe de Gobierno", W / 2, H - 86);
  ctx.fillStyle = "#BEB195";
  ctx.font = "400 28px 'JetBrains Mono', monospace";
  ctx.fillText(siteUrl.replace(/^https?:\/\//, "") + "  ·  Tampico, Tamaulipas", W / 2, H - 40);

  return new Promise((res) => canvas.toBlob(res, "image/png"));
}

async function shareRow(e, f) {
  e.stopPropagation();
  try {
    const ejeNombre = EJES.find((x) => x.id === f.ejeId)?.nombre || "";
    const blob = await buildShareCard(f, ejeNombre, window.location.origin);
    if (!blob) throw new Error("sin blob");
    const file = new File([blob], `evidencia-${f.id}.png`, { type: "image/png" });
    const text = `${f.logro}: ${f.cifra} — Verifícala en ${window.location.origin}`;
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({ files: [file], title: f.logro, text });
    } else {
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = file.name;
      a.click();
      toast.success("Tarjeta descargada, lista para compartir en redes");
    }
  } catch (err) {
    if (err?.name !== "AbortError") toast.error("No se pudo generar la tarjeta para compartir");
  }
}

export default function FactChecking({ config, query, setQuery, selected, onSelect }) {
  const [ejeFilter, setEjeFilter] = useState(0);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return FACTCHECK.filter((f) => {
      const matchEje = ejeFilter === 0 || f.ejeId === ejeFilter;
      const matchQ =
        !q ||
        f.logro.toLowerCase().includes(q) ||
        f.cifra.toLowerCase().includes(q) ||
        f.descripcion.toLowerCase().includes(q) ||
        f.id.toLowerCase().includes(q);
      return matchEje && matchQ;
    });
  }, [query, ejeFilter]);

  const openDoc = (e, url) => {
    if (url && url !== "#") return;
    e.preventDefault();
    toast.info("El documento oficial estará disponible próximamente");
  };

  const downloadPdf = (f) => {
    const url = config?.factcheck_pdfs?.[f.id];
    if (url && url !== "#") {
      window.open(url, "_blank", "noopener,noreferrer");
    } else {
      toast.info("El soporte en PDF estará disponible próximamente", { description: f.logro });
    }
  };

  return (
    <section id="fact-checking" data-testid="fact-checking-section" className="bg-arena-sand py-20 md:py-28 grain relative">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <ChapterHeading
          num="05 · Evidencias"
          title="Cada cifra, con evidencia"
          desc="Los resultados más destacados de esta administración. Conócelos y compruébalos."
        />

        <div className="flex flex-col md:flex-row md:items-center gap-4 mb-8">
          <div className="flex items-center gap-2 bg-white rounded-full border border-border pl-4 pr-2 py-1.5 w-full md:max-w-md focus-within:border-guinda transition-colors duration-200">
            <Search className="w-4 h-4 text-guinda shrink-0" aria-hidden="true" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar por logro, cifra o código…"
              aria-label="Buscar en la tabla de verificación"
              data-testid="fact-search-input"
              className="w-full bg-transparent text-sm py-1.5 focus:outline-none text-gris-charcoal placeholder:text-gris/60"
            />
          </div>
          <div className="flex flex-wrap gap-2" role="group" aria-label="Filtrar por eje">
            <button
              type="button"
              data-testid="fact-filter-todos"
              onClick={() => setEjeFilter(0)}
              aria-pressed={ejeFilter === 0}
              className={`rounded-full px-4 py-2 text-xs font-medium transition-colors duration-200 ${
                ejeFilter === 0 ? "bg-guinda text-white" : "bg-white border border-border text-gris hover:border-guinda/50"
              }`}
            >
              Todos
            </button>
            {EJES.map((e) => (
              <button
                key={e.id}
                type="button"
                data-testid={`fact-filter-eje-${e.id}`}
                onClick={() => setEjeFilter(e.id)}
                aria-pressed={ejeFilter === e.id}
                className={`rounded-full px-4 py-2 text-xs font-medium transition-colors duration-200 ${
                  ejeFilter === e.id ? "bg-guinda text-white" : "bg-white border border-border text-gris hover:border-guinda/50"
                }`}
              >
                Eje {e.id}
              </button>
            ))}
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.7, ease: EASE }}
          className="bg-white rounded-3xl border border-border overflow-hidden shadow-[0_20px_44px_-24px_rgba(90,82,74,0.4)]"
        >
          <div className="overflow-x-auto">
            <table data-testid="fact-check-table" className="w-full text-left min-w-[720px]">
              <thead>
                <tr className="border-b border-border bg-arena-light/60">
                  <th className="px-6 py-4 font-mono text-[11px] tracking-[0.2em] uppercase text-gris">Logro / Obra</th>
                  <th className="px-6 py-4 font-mono text-[11px] tracking-[0.2em] uppercase text-gris">Cifra</th>
                  <th className="px-6 py-4 font-mono text-[11px] tracking-[0.2em] uppercase text-gris">Eje</th>
                  <th className="px-6 py-4 font-mono text-[11px] tracking-[0.2em] uppercase text-gris">Estatus</th>
                  <th className="px-6 py-4 font-mono text-[11px] tracking-[0.2em] uppercase text-gris">Documento</th>
                  <th className="px-6 py-4 font-mono text-[11px] tracking-[0.2em] uppercase text-gris">Compartir</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((f) => (
                  <tr
                    key={f.id}
                    data-testid={`fact-row-${f.id}`}
                    onClick={() => onSelect(f)}
                    tabIndex={0}
                    onKeyDown={(e) => e.key === "Enter" && onSelect(f)}
                    className="border-b border-border/60 last:border-0 hover:bg-arena-light/50 cursor-pointer transition-colors duration-150 focus-visible:bg-arena-light"
                  >
                    <td className="px-6 py-4 text-sm font-medium text-gris-charcoal">{f.logro}</td>
                    <td className="px-6 py-4 font-serif text-lg text-guinda whitespace-nowrap">{f.cifra}</td>
                    <td className="px-6 py-4 text-xs text-gris whitespace-nowrap">Eje {f.ejeId}</td>
                    <td className="px-6 py-4">
                      <VerificadoBadge />
                    </td>
                    <td className="px-6 py-4">
                      <button
                        type="button"
                        data-testid={`fact-doc-${f.id}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          const url = config?.factcheck_pdfs?.[f.id];
                          if (url && url !== "#") {
                            window.open(url, "_blank", "noopener,noreferrer");
                          } else {
                            toast.info("El documento o la nota relacionada estará disponible próximamente", {
                              description: f.logro,
                            });
                          }
                        }}
                        className="inline-flex items-center gap-1.5 text-sm font-medium text-guinda hover:text-guinda-dark transition-colors duration-200"
                      >
                        Ver
                        <ExternalLink className="w-3.5 h-3.5" aria-hidden="true" />
                      </button>
                    </td>
                    <td className="px-6 py-4">
                      <button
                        type="button"
                        data-testid={`fact-share-${f.id}`}
                        onClick={(e) => shareRow(e, f)}
                        aria-label={`Compartir en redes sociales: ${f.logro}`}
                        className="inline-flex items-center gap-1.5 text-sm font-medium text-gris hover:text-guinda transition-colors duration-200"
                      >
                        <Share2 className="w-4 h-4" aria-hidden="true" />
                        Compartir
                      </button>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={5} data-testid="fact-empty-state" className="px-6 py-14 text-center text-gris text-sm">
                      No se encontraron cifras con esos criterios. Prueba con otra búsqueda.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </motion.div>
        <p className="mt-5 text-sm text-gris/80 font-mono tracking-wide" data-testid="fact-count">
          Cifras del Segundo Informe de Gobierno de Tampico
        </p>
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && onSelect(null)}>
        <DialogContent data-testid="fact-modal" className="sm:max-w-lg bg-white">
          {selected && (
            <>
              <DialogHeader>
                <p className="font-mono text-xs tracking-[0.25em] uppercase text-guinda">
                  {selected.id} · Eje {selected.ejeId} — {EJES.find((e) => e.id === selected.ejeId)?.nombre}
                </p>
                <DialogTitle className="font-serif text-2xl text-gris-charcoal leading-snug">
                  {selected.logro}
                </DialogTitle>
                <DialogDescription asChild>
                  <p className="font-serif text-4xl text-guinda pt-2">{selected.cifra}</p>
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <VerificadoBadge />
                <p className="text-sm text-gris leading-relaxed">{selected.descripcion}</p>
                <p className="text-xs font-mono text-gris/80 border-l-2 border-arena pl-3">{selected.fuente}</p>
                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                  <a
                    href={config?.informe_pdf_url && config.informe_pdf_url !== "#" ? config.informe_pdf_url : "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => openDoc(e, config?.informe_pdf_url)}
                    data-testid="fact-modal-doc-link"
                    className="inline-flex items-center justify-center gap-2 rounded-full border border-guinda text-guinda hover:bg-guinda hover:text-white px-5 py-2.5 text-sm font-medium transition-colors duration-200"
                  >
                    <ExternalLink className="w-4 h-4" aria-hidden="true" />
                    Documento oficial
                  </a>
                  <button
                    type="button"
                    data-testid="fact-modal-download-pdf"
                    onClick={() => downloadPdf(selected)}
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-guinda hover:bg-guinda-dark text-white px-5 py-2.5 text-sm font-medium transition-colors duration-200"
                  >
                    <FileDown className="w-4 h-4" aria-hidden="true" />
                    Descargar soporte PDF
                  </button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
